import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAnalyticsMetric,
  getDetailedAnalytics,
  getTransactionAnalytics,
  getTransactionDetails,
} from "../../apis/analytics";
import { cleanObject } from "../../helpers";
import { TransactionResponse } from "./types/analyticsTypes";
import moment from "moment";

interface AnalyticsState {
  loading: boolean;
  error: string;
  metricsAnalytics: any;
  detailedAnalytics: any;
  transactions: any;
  transactionDetails: TransactionResponse | null;
  startDate: Date;
  endDate: Date;
}

const initialState: AnalyticsState = {
  loading: false,
  error: "",
  metricsAnalytics: null,
  detailedAnalytics: null,
  transactions: null,
  transactionDetails: null,
  startDate: new Date(moment().startOf("day").toISOString()),
  endDate: new Date(moment().endOf("day").toISOString()),
};

// Async thunks
export const fetchAnalyticsMetricsAsync = createAsyncThunk(
  "analytics/fetchAnalyticsMetrics",
  async (_, { rejectWithValue }) => {
    try {
      const res = await getAnalyticsMetric();
      return res;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ?? "Failed to Analytics Metrics"
      );
    }
  }
);
export const fetchDetailedAnalyticsAsync = createAsyncThunk(
  "analytics/fetchDetailedAnalytics",
  async (payload: any, { rejectWithValue }) => {
    try {
      const parameters = cleanObject(payload);
      const res = await getDetailedAnalytics(parameters);
      return res;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ?? "Failed to Detailed Analytics"
      );
    }
  }
);

export const fetchTransactionAnalyticsAsync = createAsyncThunk(
  "analytics/fetchTransactionAnalytics",
  async (payload: any, { rejectWithValue }) => {
    try {
      const parameters = cleanObject(payload);
      const res = await getTransactionAnalytics(parameters);
      return res;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ?? "Failed to fetch Transaction Analytics"
      );
    }
  }
);

export const fetchTransactionDetailsAsync = createAsyncThunk(
  "analytics/fetchTransactionDetails",
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await getTransactionDetails(id);
      return res;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ?? "Failed to fetch Transaction Details"
      );
    }
  }
);

// Slice
const analyticsSlice = createSlice({
  name: "analytics",
  initialState,
  reducers: {
    clearAnalyticsData: (state) => {
      state.metricsAnalytics = null;
      state.detailedAnalytics = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all countries
      .addCase(fetchDetailedAnalyticsAsync.pending, (state) => {
        state.loading = true;
        state.error = "";
      })
      .addCase(fetchDetailedAnalyticsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.error = "";
        state.detailedAnalytics = action.payload;
      })
      .addCase(fetchDetailedAnalyticsAsync.rejected, (state) => {
        state.loading = false;
        state.error = "";
      })
      .addCase(fetchTransactionAnalyticsAsync.pending, (state) => {
        state.loading = true;
        state.error = "";
      })
      .addCase(fetchTransactionAnalyticsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.error = "";
        state.transactions = action.payload;
      })
      .addCase(fetchTransactionAnalyticsAsync.rejected, (state) => {
        state.loading = false;
        state.error = "";
      })
      .addCase(fetchAnalyticsMetricsAsync.pending, (state) => {
        state.loading = true;
        state.error = "";
      })
      .addCase(fetchAnalyticsMetricsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.error = "";
        state.metricsAnalytics = action.payload;
      })
      .addCase(fetchAnalyticsMetricsAsync.rejected, (state) => {
        state.loading = false;
        state.error = "";
      })
      .addCase(fetchTransactionDetailsAsync.pending, (state) => {
        state.loading = true;
        state.error = "";
      })
      .addCase(fetchTransactionDetailsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.error = "";
        state.transactionDetails = action.payload;
      })
      .addCase(fetchTransactionDetailsAsync.rejected, (state) => {
        state.loading = false;
        state.error = "";
      });
  },
});

export const { clearAnalyticsData } = analyticsSlice.actions;

export default analyticsSlice.reducer;
